import { CurrencyPaymentStatus } from '@/interfaces/billing'
import {
  parseAsArrayOf,
  parseAsBoolean,
  parseAsString,
  useQueryStates,
} from 'nuqs'
import { useEffect, useMemo } from 'react'

// Storage keys for different contexts
const CURRENCY_PAYMENTS_LIST_FILTERS_KEY = 'currency-payments-list-filters'

// Define the filter state interface
interface CurrencyPaymentsFilterState {
  codeCurrency: string
  dateFrom: string | null
  dateTo: string | null
  amountFrom: string | null
  amountTo: string | null
  economicCycleIds: string[]
  paymentGatewayIds: string[]
  areaSalesId: string | null
  paymentConfirmed?: boolean
  isARefund?: boolean
  isPrepaidPayment?: boolean
  isPartialPayment?: boolean
}

// Define the saved filters interface (for localStorage)
interface SavedCurrencyPaymentsFilters {
  codeCurrency?: string
  dateFrom?: string
  dateTo?: string
  amountFrom?: string
  amountTo?: string
  economicCycleIds: string[]
  paymentGatewayIds?: string[]
  areaSalesId?: string
  paymentConfirmed?: boolean
  isARefund?: boolean
  isPrepaidPayment?: boolean
  isPartialPayment?: boolean
}

// Define filter parsers based on PrepaidPaymentsQueryParams
const currencyPaymentsFilterParsers = {
  codeCurrency: parseAsString.withDefault('CUP').withOptions({
    clearOnDefault: true,
  }),
  dateFrom: parseAsString.withOptions({
    clearOnDefault: true,
  }),
  dateTo: parseAsString.withOptions({
    clearOnDefault: true,
  }),
  amountFrom: parseAsString.withOptions({
    clearOnDefault: true,
  }),
  amountTo: parseAsString.withOptions({
    clearOnDefault: true,
  }),
  economicCycleIds: parseAsArrayOf(parseAsString)
    .withDefault([])
    .withOptions({ clearOnDefault: true }),
  paymentGatewayIds: parseAsArrayOf(parseAsString)
    .withDefault([])
    .withOptions({ clearOnDefault: true }),
  areaSalesId: parseAsString.withOptions({
    clearOnDefault: true,
  }),
  paymentConfirmed: parseAsBoolean.withDefault(false),
  isARefund: parseAsBoolean.withDefault(false),
  isPrepaidPayment: parseAsBoolean.withDefault(false),
  isPartialPayment: parseAsBoolean.withDefault(false),
}

// Helper function to save filters to localStorage
const saveFiltersToStorage = (
  filters: CurrencyPaymentsFilterState,
  key: string
) => {
  try {
    // Only save non-default values to reduce storage size
    const filtersToSave = Object.entries(filters).reduce(
      (acc, [key, value]) => {
        // Skip default values
        if (
          value === '' ||
          value === 'all' ||
          value === null ||
          value === undefined ||
          (Array.isArray(value) && value.length === 0) ||
          (Array.isArray(value) && value.length === 1 && value[0] === 'all') ||
          (key === 'codeCurrency' && value === 'CUP') ||
          (key === 'dateType' && value === 'emision')
        ) {
          return acc
        }
        acc[key] = value
        return acc
      },
      {} as Record<string, unknown>
    )

    localStorage.setItem(key, JSON.stringify(filtersToSave))
  } catch {
    //
  }
}

// Helper function to get filters from localStorage
const getFiltersFromStorage = (
  key: string
): SavedCurrencyPaymentsFilters | null => {
  try {
    const stored = localStorage.getItem(key)
    return stored ? JSON.parse(stored) : null
  } catch {
    return null
  }
}
const resolveSelectedStatus = (
  filters: CurrencyPaymentsFilterState
): CurrencyPaymentStatus | null => {
  if (filters.isARefund) return 'REFUND'
  if (filters.isPrepaidPayment) return 'PREPAID_PAYMENT'
  if (filters.isPartialPayment) return 'PARTIAL_PAYMENT'
  if (filters.paymentConfirmed) return 'CONFIRMED_PAYMENT'
  return null
}

export const useCurrencyPaymentsFilters = () => {
  // Use nuqs for URL state management
  const [filters, setFilters] = useQueryStates(currencyPaymentsFilterParsers, {
    history: 'replace',
    shallow: true,
  })

  const selectedStatus = resolveSelectedStatus(filters)

  // Load filters from localStorage on mount - simple approach like billing
  useEffect(() => {
    const savedFilters = getFiltersFromStorage(
      CURRENCY_PAYMENTS_LIST_FILTERS_KEY
    )
    if (savedFilters) {
      // Merge saved filters with current state, preserving URL parameters
      setFilters((prevFilters) => ({
        ...prevFilters,
        ...savedFilters,
      }))
    }
  }, [setFilters])

  // Save filters to localStorage whenever they change
  useEffect(() => {
    saveFiltersToStorage(filters, CURRENCY_PAYMENTS_LIST_FILTERS_KEY)
  }, [filters])

  // Convert filters to CashOperationQueryParams format
  const searchParams = useMemo(() => {
    const params = new URLSearchParams()

    if (filters.codeCurrency && filters.codeCurrency !== 'CUP') {
      params.set('codeCurrency', filters.codeCurrency)
    }

    if (filters.dateFrom) {
      params.set('dateFrom', filters.dateFrom)
    }

    if (filters.dateTo) {
      params.set('dateTo', filters.dateTo)
    }

    if (filters.amountFrom) {
      params.set('amountFrom', filters.amountFrom)
    }

    if (filters.amountTo) {
      params.set('amountTo', filters.amountTo)
    }

    if (filters.economicCycleIds && filters.economicCycleIds.length > 0) {
      filters.economicCycleIds.forEach((id) =>
        params.append('economicCycleIds', id)
      )
    }

    if (filters.areaSalesId) {
      params.set('areaSalesId', filters.areaSalesId)
    }

    if (filters.paymentGatewayIds && filters.paymentGatewayIds.length > 0) {
      filters.paymentGatewayIds.forEach((id) =>
        params.append('paymentGatewayIds', id)
      )
    }

    if (filters.isARefund) params.set('isARefund', 'true')
    if (filters.isPrepaidPayment) params.set('isPrepaidPayment', 'true')
    if (filters.isPartialPayment) params.set('isPartialPayment', 'true')
    if (filters.paymentConfirmed) params.set('paymentConfirmed', 'true')

    return params.toString()
  }, [filters])

  // Helper functions for updating specific filters
  const updateCodeCurrency = (codeCurrency: string) => {
    setFilters((prev) => ({ ...prev, codeCurrency }))
  }

  const updateDateRange = (dateFrom: string | null, dateTo: string | null) => {
    setFilters((prev) => ({ ...prev, dateFrom, dateTo }))
  }

  const statusToFlags = (status: CurrencyPaymentStatus) => {
    switch (status) {
      case 'REFUND':
        return {
          isARefund: true,
          isPrepaidPayment: false,
          isPartialPayment: false,
          paymentConfirmed: false,
        }
      case 'PREPAID_PAYMENT':
        return {
          isPrepaidPayment: true,
          isARefund: false,
          isPartialPayment: false,
          paymentConfirmed: false,
        }
      case 'PARTIAL_PAYMENT':
        return {
          isPartialPayment: true,
          isARefund: false,
          isPrepaidPayment: false,
          paymentConfirmed: false,
        }
      case 'CONFIRMED_PAYMENT':
        return {
          paymentConfirmed: true,
          isARefund: false,
          isPrepaidPayment: false,
          isPartialPayment: false,
        }
      default:
        return {
          isARefund: false,
          isPrepaidPayment: false,
          isPartialPayment: false,
          paymentConfirmed: false,
        }
    }
  }

  const resetFlags = {
    paymentConfirmed: false,
    isARefund: false,
    isPrepaidPayment: false,
    isPartialPayment: false,
  }

  const updateStatus = (status: string | null) => {
    if (!status) {
      setFilters((prev) => ({ ...prev, ...resetFlags }))
      return
    }

    const flags = statusToFlags(status as CurrencyPaymentStatus)
    setFilters((prev) => ({ ...prev, ...resetFlags, ...flags }))
  }

  const updateAmount = (amountFrom: string | null, amountTo: string | null) => {
    setFilters((prev) => ({ ...prev, amountFrom, amountTo }))
  }

  const updateEconomicCycleIds = (economicCycleIds: string[]) => {
    setFilters((prev) => ({ ...prev, economicCycleIds }))
  }

  const updatePaymentGatewayIds = (paymentGatewayIds: string[]) => {
    setFilters((prev) => ({ ...prev, paymentGatewayIds }))
  }

  const updateAreaSalesId = (areaSalesId: string | null) => {
    setFilters((prev) => ({ ...prev, areaSalesId }))
  }

  // Individual clear functions
  const clearCodeCurrencyFilter = () => {
    setFilters((prev) => ({ ...prev, codeCurrency: 'CUP' }))
  }

  const clearDateRangeFilter = () => {
    setFilters((prev) => ({ ...prev, dateFrom: null, dateTo: null }))
  }

  const clearStatusFilter = () => {
    setFilters((prev) => ({
      ...prev,
      paymentConfirmed: false,
      isARefund: false,
      isPrepaidPayment: false,
      isPartialPayment: false,
    }))
  }

  const clearAmountFilter = () => {
    setFilters((prev) => ({ ...prev, amountFrom: null, amountTo: null }))
  }

  const clearEconomicCycleIdsFilter = (id?: string) => {
    if (id) {
      // quitar solo ese id
      setFilters((prev) => ({
        ...prev,
        economicCycleIds: (prev.economicCycleIds ?? []).filter((x) => x !== id),
      }))
    } else {
      // limpiar todo
      setFilters((prev) => ({ ...prev, economicCycleIds: [] }))
    }
  }

  const clearPaymentGatewayIdsFilter = (id?: string) => {
    if (id) {
      // quitar solo ese id
      setFilters((prev) => ({
        ...prev,
        paymentGatewayIds: (prev.paymentGatewayIds ?? []).filter(
          (x) => x !== id
        ),
      }))
    } else {
      // limpiar todo
      setFilters((prev) => ({ ...prev, paymentGatewayIds: [] }))
    }
  }

  const clearAreaSalesIdFilter = () => {
    setFilters((prev) => ({ ...prev, areaSalesId: null }))
  }

  const clearAllFilters = () => {
    // Limpieza consistente con tus parsers (arrays -> [])
    setFilters({
      codeCurrency: 'CUP',
      dateFrom: null,
      dateTo: null,
      amountFrom: null,
      amountTo: null,
      paymentGatewayIds: [],
      economicCycleIds: [],
      areaSalesId: null,
      paymentConfirmed: false,
      isARefund: false,
      isPrepaidPayment: false,
      isPartialPayment: false,
    })

    // Limpia storage
    try {
      localStorage.removeItem(CURRENCY_PAYMENTS_LIST_FILTERS_KEY)
    } catch {
      //
    }
  }

  // Check if any filters are active
  const hasActiveFilters = useMemo(() => {
    return (
      (filters.codeCurrency && filters.codeCurrency !== 'CUP') ||
      !!filters.dateFrom ||
      !!filters.dateTo ||
      !!selectedStatus || // estado solo UI
      !!filters.amountFrom ||
      !!filters.amountTo ||
      (filters.economicCycleIds && filters.economicCycleIds.length > 0) ||
      (filters.paymentGatewayIds && filters.paymentGatewayIds.length > 0) ||
      !!filters.areaSalesId ||
      filters.paymentConfirmed ||
      filters.isARefund ||
      filters.isPrepaidPayment ||
      filters.isPartialPayment
    )
  }, [filters, selectedStatus])

  return {
    filters,
    searchParams,

    // UI status
    selectedStatus,
    updateStatus,

    // updates
    updateCodeCurrency,
    updateDateRange,
    updateAmount,
    updateEconomicCycleIds,
    updatePaymentGatewayIds,
    updateAreaSalesId,

    // clears
    clearCodeCurrencyFilter,
    clearDateRangeFilter,
    clearStatusFilter,
    clearAmountFilter,
    clearEconomicCycleIdsFilter,
    clearPaymentGatewayIdsFilter,
    clearAreaSalesIdFilter,
    clearAllFilters,

    // helpers
    hasActiveFilters,
    setFilters,
  }
}

export type CurrencyPaymentsFilters = ReturnType<
  typeof useCurrencyPaymentsFilters
>['filters']
export type CurrencyPaymentsFilterKey =
  keyof typeof currencyPaymentsFilterParsers
